import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { CHANNELS, SERIES, type CanvasPoint, type Channel, type Format, type Series, type Status } from '../api'
import { ChannelIcon } from '../components/ChannelIcon'
import { SeriesDot } from '../components/SeriesDot'
import { Sheet } from '../components/Sheet'
import { StatusIcon } from '../components/StatusIcon'
import { formatsFor } from '../domain/formats'
import { guessChannels, guessFormat, guessSeries } from '../domain/guess'
import { CHANNEL_LABEL, FORMAT_LABEL, LOOP_ORDER, SERIES_LABEL, STATUS_LABEL } from '../domain/labels'
import { createIdea } from '../state/ideas'
import { createStore, useStore } from '../state/store'
import './quick-add.css'

/**
 * The quick add sheet: one field, three chips, Save.
 *
 * Ideas come in bursts, so saving empties the field and keeps the sheet and
 * the keyboard up for the next one. The first line is the title; anything
 * after a Shift+Enter is the body. The chips show what the title implies as it
 * is typed, and a tap cycles them; the idea is saved with exactly what the
 * chips showed.
 *
 * The channel chip picks where the idea goes, and the format chip cycles only
 * that channel's formats: eight for a short, six for YouTube, one for
 * LinkedIn. Started from the LinkedIn list, the sheet opens on LinkedIn.
 * Started with a double-click on the desktop canvas, the first idea saved
 * lands where the double-click was, and the rest of the burst joins the end
 * of the frame.
 */

const quickAdd = createStore<{ open: boolean; status: Status; channel?: Channel; canvas?: CanvasPoint }>({
  open: false,
  status: 'idea',
})

/** Registered by the mounted sheet, so `openQuickAdd` can focus inside the tap. */
const elements: { layer: HTMLDivElement | null; field: HTMLTextAreaElement | null } = {
  layer: null,
  field: null,
}

/**
 * Open the sheet. Call it straight from a tap handler: the field is focused
 * synchronously, before React renders, because that is the only kind of focus
 * iOS answers by raising the keyboard.
 */
export function openQuickAdd(status: Status = 'idea', channel?: Channel, canvas?: CanvasPoint): void {
  if (elements.layer) elements.layer.inert = false
  elements.field?.focus({ preventScroll: true })
  quickAdd.set({ open: true, status, channel, canvas })
}

export function closeQuickAdd(): void {
  quickAdd.set((state) => ({ ...state, open: false }))
}

/**
 * Tapping a chip or Save must not take focus from the field: on a phone that
 * drops the keyboard mid-burst. Cancelling `mousedown` is what stops the focus
 * moving, on iOS as well as on a laptop; the click still fires.
 */
const keepFocus = { onMouseDown: (event: { preventDefault: () => void }) => event.preventDefault() }

function cycle<T>(values: readonly T[], current: T): T {
  return values[(values.indexOf(current) + 1) % values.length]
}

export function splitDraft(draft: string): { title: string; body: string } {
  const [first = '', ...rest] = draft.split('\n')
  return { title: first.trim(), body: rest.join('\n').trim() }
}

export function QuickAdd() {
  const { open, status: presetStatus, channel: presetChannel } = useStore(quickAdd)
  const [draft, setDraft] = useState('')
  const [series, setSeries] = useState<Series | null>(null)
  const [format, setFormat] = useState<Format | null>(null)
  const [channels, setChannels] = useState<Channel[] | null>(null)
  const [status, setStatus] = useState<Status>(presetStatus)
  const [saved, setSaved] = useState<{ count: number; last: string } | null>(null)
  const field = useRef<HTMLTextAreaElement>(null)

  const { title, body } = splitDraft(draft)
  const shownSeries = series ?? guessSeries(title)
  const shownFormat = format ?? guessFormat(title, channels ?? undefined)
  const shownChannels = channels ?? guessChannels(shownFormat)

  useEffect(() => {
    if (open) {
      setStatus(presetStatus)
      setChannels(presetChannel ? [presetChannel] : null)
    } else setSaved(null)
  }, [open, presetStatus, presetChannel])

  const cycleChannel = () => {
    const next = cycle(CHANNELS, shownChannels[0])
    setChannels([next])
    // A format picked for the old channel does not carry over to a new one.
    if (format && !formatsFor([next]).includes(format)) setFormat(null)
  }

  // Grow with the text up to a cap, then scroll inside.
  useEffect(() => {
    const element = field.current
    if (!element) return
    element.style.height = 'auto'
    element.style.height = `${Math.min(element.scrollHeight, 168)}px`
  }, [draft, open])

  const setField = useCallback((element: HTMLTextAreaElement | null) => {
    field.current = element
    elements.field = element
  }, [])
  const setLayer = useCallback((element: HTMLDivElement | null) => {
    elements.layer = element
  }, [])

  const save = () => {
    if (!title) return
    void createIdea({
      title,
      body: body || undefined,
      series: shownSeries,
      format: shownFormat,
      channels: shownChannels,
      source: 'phone',
      status,
      canvas: quickAdd.get().canvas,
    })
    quickAdd.set((state) => ({ ...state, canvas: undefined }))
    setSaved((previous) => ({ count: (previous?.count ?? 0) + 1, last: title }))
    setDraft('')
    setSeries(null)
    setFormat(null)
    // The channel stays for the next one, like the status: bursts are usually
    // all for the same place.
    // Focus never left the field, so the keyboard stays up for the next one.
    field.current?.focus({ preventScroll: true })
  }

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return
    event.preventDefault()
    save()
  }

  const close = useCallback(() => {
    field.current?.blur()
    closeQuickAdd()
  }, [])

  return (
    <Sheet open={open} onClose={close} label="New idea" keepMounted layerRef={setLayer}>
      <div className="quick-add">
        <label className="visually-hidden" htmlFor="quick-add-field">
          New idea
        </label>
        <textarea
          id="quick-add-field"
          ref={setField}
          className="quick-add-field"
          rows={1}
          value={draft}
          placeholder="What's the idea?"
          enterKeyHint="send"
          autoCapitalize="sentences"
          autoComplete="off"
          spellCheck
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={onKeyDown}
        />

        <div className="quick-add-chips" role="group" aria-label="Properties">
          <button
            type="button"
            className="chip"
            data-guess={series === null}
            data-series={shownSeries}
            aria-label={`Series: ${SERIES_LABEL[shownSeries]}${series === null ? ', guessed' : ''}. Tap to change.`}
            {...keepFocus}
            onClick={() => setSeries(cycle(SERIES, shownSeries))}
          >
            <SeriesDot series={shownSeries} />
            {SERIES_LABEL[shownSeries]}
          </button>
          <button
            type="button"
            className="chip"
            data-guess={channels === null}
            aria-label={`For ${shownChannels.map((c) => CHANNEL_LABEL[c]).join(' and ')}${channels === null ? ', guessed' : ''}. Tap to change.`}
            {...keepFocus}
            onClick={cycleChannel}
          >
            {shownChannels.map((channel) => (
              <ChannelIcon key={channel} channel={channel} size={15} />
            ))}
            {shownChannels.map((channel) => CHANNEL_LABEL[channel]).join(' + ')}
          </button>
          <button
            type="button"
            className="chip chip-mono"
            data-guess={format === null}
            aria-label={`Format: ${FORMAT_LABEL[shownFormat]}${format === null ? ', guessed' : ''}. Tap to change.`}
            {...keepFocus}
            onClick={() => setFormat(cycle(formatsFor(shownChannels), shownFormat))}
          >
            {FORMAT_LABEL[shownFormat]}
          </button>
        </div>

        {/* Todoist's footer: where it lands on the left, send on the right. */}
        <div className="quick-add-footer">
          <button
            type="button"
            className="quick-add-status"
            aria-label={`Adds to ${STATUS_LABEL[status]}. Tap to change.`}
            {...keepFocus}
            onClick={() => setStatus(cycle(LOOP_ORDER, status))}
          >
            <StatusIcon status={status} size={14} />
            {STATUS_LABEL[status]}
          </button>
          <p className="quick-add-note" aria-live="polite">
            {saved ? (
              <>
                <span className="quick-add-saved">
                  {saved.count === 1 ? 'Saved' : `${saved.count} saved`}
                </span>
                <span className="quick-add-last">{saved.last}</span>
              </>
            ) : (
              <span className="quick-add-hint">
                <kbd>Enter</kbd> saves, <kbd>Shift</kbd>+<kbd>Enter</kbd> adds notes
              </span>
            )}
          </p>
          <button
            type="button"
            className="button button-primary quick-add-save"
            disabled={!title}
            {...keepFocus}
            onClick={save}
          >
            Save
          </button>
        </div>
      </div>
    </Sheet>
  )
}
